import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import { router } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import AsyncStorage from "@react-native-async-storage/async-storage";
import i18n, { availableLocales } from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import { authActions } from "../../store/slices/authSlice";
import { appActions, selectIsRTL } from "../../store/slices/appSlice";

// Import images
import SignupImage from "../../assets/images/stock.png";

interface SignUpData {
  username: string;
  gmail: string;
  storename: string;
  Password: string;
  phone: string;
}

const WebSignupScreen: React.FC = () => {
  const dispatch = useDispatch();
  const [showLanguages, setShowLanguages] = useState(false);
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Use Redux selector for locale and RTL - single source of truth (reactive)
  const locale = useSelector((state: any) => state.app.locale);
  const isRTL = useSelector(selectIsRTL);

  const EmailRegx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      gmail: "",
      storename: "",
      Password: "",
      phone: "",
    },
  });

  const handleSignUp = async (data: SignUpData) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post(`/users/register`, {
        username: data.username,
        email: data.gmail,
        password: data.Password,
        storename: data.storename,
        phone: data.phone,
      });

      Toast.show(response.data.message || i18n.t("registrationSuccessful"), {
        type: "success",
      });

      try {
        const loginResponse = await customAxios.post(`/users/login`, {
          email: data.gmail,
          password: data.Password,
        });

        const token = loginResponse.data.token;
        const userData = loginResponse.data.user;

        await AsyncStorage.setItem("token", token);
        await AsyncStorage.setItem("user", JSON.stringify(userData));

        dispatch(authActions.setUser(userData));

        Toast.show(i18n.t("verifyEmailReminder"), {
          type: "info",
          duration: 6000,
        });

        setButtonSpinner(false);
        router.push("/(drawer)/start");
      } catch (loginError: any) {
        setButtonSpinner(false);
        Toast.show(i18n.t("pleaseLoginManually"), { type: "warning" });
        router.push("/(routes)/(auth)/login");
      }
    } catch (error: any) {
      setButtonSpinner(false);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("unexpectedError");
      Toast.show(errorMessage, { type: "danger" });
    }
  };

  const handleLanguageChange = async (languageCode: string) => {
    setShowLanguages(false);
    i18n.locale = languageCode;
    dispatch(appActions.setLocale(languageCode));
    // Save to localStorage for sync read on next load
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem("appLocale", languageCode);
    }
    await AsyncStorage.setItem("appLocale", languageCode);
    // No reload needed - Redux state change triggers re-render
  };

  return (
    <View
      style={[
        styles.container,
        { flexDirection: isRTL ? "row-reverse" : "row" },
      ]}
    >
      {/* Left Panel - Branding */}
      <View style={styles.leftPanel}>
        <View style={styles.brandingContent}>
          <Image
            source={require("../../assets/logo-no-bg-white.png")}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.brandTitle}>خدام</Text>
          <Text style={styles.brandSubtitle}>
            {i18n.t("welcomeIntoDesc1")}
          </Text>
          <Image
            source={SignupImage}
            style={styles.illustrationImage}
            resizeMode="contain"
          />
        </View>
      </View>

      {/* Right Panel - Signup Form */}
      <ScrollView
        style={styles.rightPanel}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.formContainer}>
          {/* Language Selector */}
          <View style={styles.languageSelector}>
            <TouchableOpacity
              style={styles.languageButton}
              onPress={() => setShowLanguages(!showLanguages)}
            >
              <Text style={styles.languageButtonText}>🌐</Text>
              <Text style={styles.languageButtonLabel}>
                {availableLocales.find((l) => l.code === locale)?.name ||
                  locale}
              </Text>
            </TouchableOpacity>

            {showLanguages && (
              <View style={styles.languageDropdown}>
                {availableLocales.map((lang) => (
                  <TouchableOpacity
                    key={lang.code}
                    style={[
                      styles.languageOption,
                      locale === lang.code && styles.languageOptionActive,
                    ]}
                    onPress={() => handleLanguageChange(lang.code)}
                  >
                    <Text
                      style={[
                        styles.languageOptionText,
                        locale === lang.code && styles.languageOptionTextActive,
                      ]}
                    >
                      {lang.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Header */}
          <View style={styles.formHeader}>
            <Text style={styles.formTitle}>
              {i18n.t("signupTitle")}
            </Text>
            <Text style={styles.formSubtitle}>
              {i18n.t("signupDesc")}
            </Text>
          </View>

          {/* Username Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {i18n.t("username")}
            </Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="user"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="username"
                rules={{ required: i18n.t("usernameRequired") }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder={i18n.t("username")}
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </View>
            {errors.username && (
              <Text style={styles.errorText}>{errors.username.message}</Text>
            )}
          </View>

          {/* Email Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {i18n.t("email")}
            </Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="mail"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="gmail"
                rules={{
                  required: i18n.t("mailRequired"),
                  pattern: {
                    value: EmailRegx,
                    message: i18n.t("invalidEmail"),
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder={i18n.t("email")}
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType="email-address"
                  />
                )}
              />
            </View>
            {errors.gmail && (
              <Text style={styles.errorText}>{errors.gmail.message}</Text>
            )}
          </View>

          {/* Password Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {i18n.t("password")}
            </Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="lock"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="Password"
                rules={{
                  required: i18n.t("passwordRequired"),
                  minLength: { value: 3, message: i18n.t("passwordMinLength") },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder="••••••••"
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    secureTextEntry={!showPassword}
                  />
                )}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                <Feather
                  name={showPassword ? "eye" : "eye-off"}
                  size={20}
                  color="#999"
                />
              </TouchableOpacity>
            </View>
            {errors.Password && (
              <Text style={styles.errorText}>{errors.Password.message}</Text>
            )}
          </View>

          {/* Store Name Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {i18n.t("storeName")}
            </Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="shopping-bag"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="storename"
                rules={{ required: i18n.t("storeNameRequired") }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder={i18n.t("storeName")}
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </View>
            {errors.storename && (
              <Text style={styles.errorText}>{errors.storename.message}</Text>
            )}
          </View>

          {/* Phone Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {i18n.t("phoneNumber")}
            </Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="phone"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="phone"
                rules={{
                  required: i18n.t("phoneRequired"),
                  pattern: {
                    value: /^\d{10}$/,
                    message: i18n.t("invalidPhoneNumber"),
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder="0779966025"
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    keyboardType="phone-pad"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </View>
            {errors.phone && (
              <Text style={styles.errorText}>{errors.phone.message}</Text>
            )}
          </View>

          {/* Signup Button */}
          <TouchableOpacity
            style={styles.signupButton}
            onPress={handleSubmit(handleSignUp)}
            disabled={buttonSpinner}
          >
            {buttonSpinner ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.signupButtonText}>
                {i18n.t("signup")}
              </Text>
            )}
          </TouchableOpacity>

          {/* Login Link */}
          <View style={styles.loginLinkContainer}>
            <Text style={styles.loginText}>
              {i18n.t("alreadyHaveAccount")}
            </Text>
            <TouchableOpacity
              onPress={() => router.push("/(routes)/(auth)/login")}
            >
              <Text style={styles.loginLink}>
                {i18n.t("signIn")}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    minHeight: "100vh" as any,
    backgroundColor: "#F8F9FA",
  },
  leftPanel: {
    flex: 1,
    backgroundColor: "#004E6D",
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  brandingContent: {
    alignItems: "center",
    maxWidth: 400,
  },
  logo: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  brandTitle: {
    fontSize: 56,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 16,
  },
  brandSubtitle: {
    fontSize: 20,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 28,
    marginBottom: 40,
  },
  illustrationImage: {
    width: 280,
    height: 220,
  },
  rightPanel: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
    paddingVertical: 60,
  },
  formContainer: {
    width: "100%",
    maxWidth: 420,
  },
  languageSelector: {
    position: "absolute" as any,
    top: -40,
    right: 0,
    zIndex: 100,
  },
  languageButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0F0F0",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 8,
  },
  languageButtonText: {
    fontSize: 20,
  },
  languageButtonLabel: {
    fontSize: 16,
    color: "#333",
    fontWeight: "500",
  },
  languageDropdown: {
    position: "absolute" as any,
    top: 44,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 5,
    minWidth: 140,
    overflow: "hidden",
    zIndex: 9999,
  },
  languageOption: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  languageOptionActive: {
    backgroundColor: "#E8F4F8",
  },
  languageOptionText: {
    fontSize: 16,
    color: "#333",
  },
  languageOptionTextActive: {
    color: "#004E6D",
    fontWeight: "600",
  },
  formHeader: {
    marginBottom: 28,
  },
  formTitle: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#004E6D",
    marginBottom: 8,
    textAlign: "center",
  },
  formSubtitle: {
    fontSize: 18,
    color: "#666",
    textAlign: "center",
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
    marginBottom: 8,
    textAlign: "right",
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 50,
  },
  inputIcon: {
    marginRight: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
    outlineStyle: "none" as any,
  },
  errorText: {
    color: "#E53935",
    fontSize: 14,
    marginTop: 4,
    textAlign: "right",
  },
  signupButton: {
    backgroundColor: "#004E6D",
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    marginTop: 8,
    marginBottom: 24,
    shadowColor: "#004E6D",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  signupButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "600",
  },
  loginLinkContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
  },
  loginText: {
    fontSize: 16,
    color: "#666",
  },
  loginLink: {
    fontSize: 16,
    color: "#004E6D",
    fontWeight: "600",
  },
});

export default WebSignupScreen;
